import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '_store';
import { apiBase } from 'api/client';
import { useActivityFrame } from '_components';
import { ThemeContext } from 'theme';

export { Shaping };

function Shaping() {
    const activityFrameRef = useActivityFrame();
    const [grafanaUrl, setGrafanaUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const { authUser, selectedOrganization } = useContext(AuthContext);
    const { resolvedTheme } = useContext(ThemeContext);

    useEffect(() => {
        let cancelled = false;

        async function bootstrapAndLoad() {
            if (!authUser) {
                // Clear iframe when logged out
                setGrafanaUrl('');
                return;
            }

            try {
                setLoading(true);
                const defaultTimeRange = authUser?.default_time_range || '24';
                const timeRangeStr = defaultTimeRange <= 24 ? `now-${defaultTimeRange}h` : `now-${defaultTimeRange/24}d`;

                const cacheBuster = Date.now();
                const dashUrl = `${apiBase()}/gf/d/fdyv1v8kaz280b/client-dashboard?kiosk&hideLogo=true&theme=${resolvedTheme}&from=${timeRangeStr}&to=now&_=${cacheBuster}`;
                if (!cancelled) setGrafanaUrl(dashUrl);
            } catch (err) {
                console.error('Grafana session bootstrap error:', err);
                if (!cancelled) setGrafanaUrl('');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        bootstrapAndLoad();
        return () => {
            cancelled = true;
        };
    }, [authUser, resolvedTheme, selectedOrganization]);

    if (!grafanaUrl && authUser && !loading) {
        return (
            <div className="flex justify-center items-center" style={{ height: 'calc(100vh - 72px)' }}>
                <div className="text-center">
                    <div className="rounded p-4" style={{ background: 'var(--color-loading-error-bg)', border: '1px solid var(--color-loading-error-border)', color: 'var(--color-loading-error-text)' }} role="alert">
                        <h5 className="font-semibold mb-1">Loading Error</h5>
                        <p className="mb-0">Unable to load Grafana dashboard.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!grafanaUrl || loading) {
        return (
            <div className="flex justify-center items-center" style={{ height: 'calc(100vh - 72px)' }}>
                <div className="text-center">
                    <span className="inline-block animate-spin rounded-full h-8 w-8 border-4" style={{ borderColor: 'var(--color-loading-spinner)', borderTopColor: 'transparent' }} role="status">
                        <span className="sr-only">Loading Grafana...</span>
                    </span>
                    <p className="mt-3">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', borderBottom: '1px solid var(--color-header-divider)', background: 'var(--color-surface-bg)', flexShrink: 0 }}>
                <span style={{ width: '0.375rem', height: '0.375rem', borderRadius: '50%', backgroundColor: 'var(--color-accent-teal)', display: 'inline-block', flexShrink: 0 }}></span>
                <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, color: 'var(--color-text-muted)', fontFamily: 'var(--app-font-stack)' }}>Shaping</span>
            </div>
            <iframe
                ref={activityFrameRef}
                key={grafanaUrl}
                src={grafanaUrl}
                width="100%"
                style={{ flex: 1, display: 'block', minHeight: 0 }}
                frameBorder="0"
                title="Grafana Dashboard"
                allow="fullscreen"
                sandbox="allow-scripts allow-forms allow-top-navigation allow-storage-access-by-user-activation allow-same-origin"
                referrerPolicy="strict-origin-when-cross-origin"
            ></iframe>
        </div>
    );
}
